import { useQuery } from '@tanstack/react-query'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { useNavigate } from 'react-router'
import { useState, useMemo, useEffect } from 'react'
import { organizationQueries } from 'src/services/user'
import { CustomSelect } from '../components/custom-select'
import { SelectOption } from 'src/services/base-model'
import { Button } from 'src/components/button'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { FilterPane } from 'src/components/layout/filter-pane'
import { ContentPane } from 'src/components/layout/content-pane'
import { PaginationComponent } from 'src/components/pagination'
import { ReportPreviewSidebar } from 'src/components/reports/report-preview-sidebar'
import { ExpenseReport } from 'src/services/expense-reports'
import { usePermissions } from 'src/hooks/use-permissions'
import { usePagination } from 'src/hooks/use-pagination'
import { useTranslation } from 'react-i18next'
import { FiFileText } from 'react-icons/fi'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { useViewMode } from 'src/hooks/use-view-mode'
import { ReportCollectionItem } from 'src/components/reports/report-collection-item'
import { AdjustmentsVerticalIcon, ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline'

export const ReportsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [viewMode, setViewMode] = useViewMode('expense-reports', 'list')
  const [organization, setOrganization] = useState<SelectOption[]>([])
  const [search, setSearch] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const { pagination, paginationCallback, goToNextPage, goToPrevPage, goToPage, resetPagination } =
    usePagination()

  useEffect(() => {
    resetPagination()
  }, [search, organization, resetPagination])

  const { data, isLoading } = useQuery(
    expenseReportQueries.list({
      filters: { organization: organization.map((o) => o.value), search },
      pagination,
      paginationCallback,
    }),
  )
  const { data: organizations, isLoading: loadingOrganizations } = useQuery(
    organizationQueries.list({ filters: { search: orgSearch } }),
  )
  const [selectedReport, setSelectedReport] = useState<ExpenseReport | null>(null)

  const handleViewReport = (report: ExpenseReport) => {
    setSelectedReport(report)
  }

  const handleCloseSidebar = () => {
    setSelectedReport(null)
  }

  const handleEditReport = (id: string) => {
    setSelectedReport(null)
    navigate(`/expense-reports/report/${id}`)
  }

  const handleCreateReport = () => {
    navigate('/expense-reports/report')
  }

  const orgOpts = useMemo(() => {
    return (
      organizations?.results.map((org) => ({
        label: org.name,
        value: org.id,
      })) || []
    )
  }, [organizations?.results])

  return (
    <>
      <PageHeader
        title={
          <>
            <FiFileText className="inline h-5 w-5" /> {t('reportsPage.title')}
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              icon={<AdjustmentsVerticalIcon />}
              onClick={() => navigate('/export-templates')}
            >
              {t('reportsPage.manageTemplates')}
            </Button>
            <Button
              variant="secondary"
              icon={<ArrowUpTrayIcon />}
              onClick={() => navigate(`/receipts`)}
            >
              {t('reportsPage.uploadReceipts')}
            </Button>
            {hasPermission('reports.create') && (
              <Button variant="primary" icon={<PlusIcon />} onClick={handleCreateReport}>
                {t('reportsPage.addReport')}
              </Button>
            )}
          </>
        }
      >
        <input
          type="text"
          placeholder={t('reportsPage.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full min-w-[120px] rounded-full border border-border-strong px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      </PageHeader>
      <MainContent>
        <FilterPane title={t('common.filters')} onClear={() => setOrganization([])}>
          <h3 className="mb-2 mt-4 text-base font-semibold">{t('reportsPage.organization')}</h3>
          <CustomSelect
            searchable
            options={orgOpts}
            values={organization}
            onChange={(values) => setOrganization(values)}
            multi
            clearable
            loading={loadingOrganizations}
            searchFn={({ state }) => {
              setOrgSearch(state.search)
              return orgOpts
            }}
          />
        </FilterPane>
        <ContentPane>
          <ListToolbar count={data?.count} viewMode={viewMode} onViewModeChange={setViewMode} />
          {isLoading && <p>{t('common.loading')}</p>}
          {data && (
            <ItemCollection viewMode={viewMode}>
              {data.results.map((report) => (
                <ReportCollectionItem
                  key={report.id}
                  report={report}
                  viewMode={viewMode}
                  onClick={() => handleViewReport(report)}
                />
              ))}
            </ItemCollection>
          )}
          <PaginationComponent
            pagination={pagination}
            isLoading={isLoading}
            onNextPage={goToNextPage}
            onPrevPage={goToPrevPage}
            onGoToPage={goToPage}
            className="mt-4"
          />
        </ContentPane>
      </MainContent>
      <ReportPreviewSidebar
        report={selectedReport}
        isOpen={!!selectedReport}
        onClose={handleCloseSidebar}
        onEdit={handleEditReport}
      />
    </>
  )
}
